import * as THREE from 'three';
import React from 'react';
import EffectorBase, { EffectorPropertyDrawer, LoopableEffector } from "./EffectorBase";
import { EasingFunctions, PropertyTypes } from "../../../globals";
import {CustomMaterials} from "../../CustomMaterials";
import ColorProperty from "../../../components/properties/ColorProperty";
import NumberProperty from "../../../components/properties/NumberProperty";
import Vector3Property from "../../../components/properties/Vector3Property";
import SceneObjectUtils from '../../utilities/SceneObjectUtils';
import { TimelineFragment } from '../TimelineFragment';
import HighlightSphere from '../../HighlightSphere';
import { areArraysEqual } from '../../utilities/Misc';
import store from '../../../state/store';

interface EffectorHighlightSphereSaveData
{
	color: string;
	scale: number;
	offset: { x: number, y: number, z: number };
	loopCount: number;
}

export default class EffectorHighlightSphere extends EffectorBase<EffectorHighlightSphere> implements LoopableEffector
{
	scale: number;
	inputColorHex: string;
	color: THREE.Color;
	offset: THREE.Vector3;
	sphereGeometry: THREE.SphereGeometry;
	sphereMaterial: THREE.ShaderMaterial;
	highlightSpheres: HighlightSphere[];
	loopCount: number;
	defaultValues: { scale: number, offset: { x: number, y: number, z: number } };


	constructor(fragment: TimelineFragment)
	{
		super(fragment);

		const timelinePrefenreces = store.getState().preferencesModule.editorPreferences;
		const defaultScale = timelinePrefenreces.defaultHighlightSphereSize || 0.1;
		const defaultColor = timelinePrefenreces.timebarColors.highlightSphere || "#00ffff";

		this.scale = defaultScale;
		this.inputColorHex = defaultColor;
		this.color = new THREE.Color();
		this.offset = new THREE.Vector3(0, 0, 0);
		this.sphereGeometry = new THREE.SphereGeometry(0.5, 32, 32);
		this.sphereMaterial = CustomMaterials.getGhostMaterial().clone();
		this.sphereMaterial.depthTest = false;
		this.highlightSpheres = [];
		this.loopCount = 1;

		this.defaultValues = {
			scale: defaultScale,
			offset: { x: 0, y: 0, z: 0 },
		};

		this.setHighlightColor(this.inputColorHex);
	}

	save = () : EffectorHighlightSphereSaveData =>
	{
		const output = {
			color: this.color.getHexString(),
			scale: this.scale,
			offset: { x: this.offset.x, y: this.offset.y, z: this.offset.z },
			loopCount: this.loopCount,
		};

		return output;
	};

	load = (savedData: EffectorHighlightSphereSaveData) =>
	{
		this.setHighlightColor(savedData.color);
		this.scale = (isNaN(savedData.scale)) ? this.defaultValues.scale : savedData.scale;
		if (savedData.offset) this.offset.set(savedData.offset.x, savedData.offset.y, savedData.offset.z);
		if (savedData.loopCount) this.loopCount = savedData.loopCount;

	};

	copyValues = (sourceEffector: EffectorHighlightSphere) =>
	{
		this.setHighlightColor(sourceEffector.color.getHexString());
		this.scale = sourceEffector.scale;
		this.offset.copy(sourceEffector.offset);
		this.loopCount = sourceEffector.loopCount;
	}

	clearSpheres = () => 
	{
		for (const highlightSphere of this.highlightSpheres)
		{
			SceneObjectUtils.disposeObject(highlightSphere);
		}
		this.highlightSpheres.length = 0;
	}

	setHighlightColor = (colorHex: string) =>
	{
		this.inputColorHex = colorHex;
		if (colorHex[0] === '#')
		{
			colorHex = colorHex.substr(1);
			colorHex = '0x' + colorHex;
		}
		else if (colorHex.length === 6)
		{
			colorHex = '0x' + colorHex;
		}
		this.color.setHex(parseInt(colorHex));
		this.sphereMaterial.uniforms["u_ghostColor"].value.copy(this.color);
	}

	setOffset = (x: number, y: number, z: number) =>
	{
		if (typeof (x) === "number" && !isNaN(x)) this.offset.setX(x);
		if (typeof (y) === "number" && !isNaN(y)) this.offset.setY(y);
		if (typeof (z) === "number" && !isNaN(z)) this.offset.setZ(z);
	}

	setscale = (value: number) =>
	{
		this.scale = value;
	}

	onAdded = () => 
	{

	}

	resetState = () =>
	{
	}

	update = (fragmentTime: number ) =>
	{
		if (fragmentTime)
		{
			let index = 0;
			this.forEachTargetObject((targetObject) =>
			{
				if (!targetObject.userData.removedFromScene)
				{
					if (this.highlightSpheres.length < index+1)
					{
						const highlightSphere = new HighlightSphere(this.sphereGeometry, this.sphereMaterial);
						targetObject.attach(highlightSphere);
						this.highlightSpheres.push(highlightSphere);
					}

					this.highlightSpheres[index].position.copy(this.offset);
					this.highlightSpheres[index].setScale(this.scale * fragmentTime);
					this.sphereMaterial.uniforms["u_alphaMultiplier"].value = 1 - EasingFunctions.easeInQuart(fragmentTime);
					index++;
				}
			});
		}
		else if (this.highlightSpheres.length)
		{
			this.clearSpheres();
		}
	}

	onDelete = () =>
	{
		super.onDelete();
		this.clearSpheres();
	}

	getEditorProperties = () =>
	{
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Highlight Color",
				PropertyTypes.Color,
				<ColorProperty
					value={this.inputColorHex}
				/>,
				() => {return this.inputColorHex;},
				(v) => {this.setHighlightColor(v);},
			),
			new EffectorPropertyDrawer(
				"Diameter",
				PropertyTypes.Number,
				<NumberProperty
					unitScaling={true}
					value={this.scale}
					min={0.0}
					step={0.1}
					defaultValue={this.defaultValues.scale}
					decimals={4}
				/>,
				() => {return this.scale;},
				(v) => {this.setscale(v);},
			),
			new EffectorPropertyDrawer(
				"Offset",
				PropertyTypes.Vector3,
				<Vector3Property
					unitScaling={true}
					value={[this.offset.x, this.offset.y, this.offset.z]}
					defaultValue={this.defaultValues.offset}
					step={0.1}
					renderChildrenInSeparateRows={true}
					decimals={3}
				/>,
				() => {return [this.offset.x, this.offset.y, this.offset.z];},
				(v) => {this.setOffset(v.x, v.y, v.z);},
				(a, b) => {return areArraysEqual(a, b);},
			),
		];
	}
}


